import {
  AGENT_ID_PREFIX,
  AGENT_VERSION_ID_PREFIX,
  CONFIG_ID_PREFIX,
  DATASET_ID_PREFIX,
  DATASET_LEGACY_ID_PREFIX,
  DATASET_VERSION_ID_PREFIX,
  EVALUATOR_ID_PREFIX,
  EVALUATOR_LEGACY_ID_PREFIX,
  EVALUATOR_VERSION_ID_PREFIX,
  FLOW_ID_PREFIX,
  FLOW_VERSION_ID_PREFIX,
  PROMPT_ID_PREFIX,
  PROMPT_VERSION_ID_PREFIX,
  TOOL_ID_PREFIX,
  TOOL_VERSION_ID_PREFIX,
} from "@/lib/constants";
import { AgentToolRequest } from "@/types/app/agent";
import { FileType } from "@/types/app/file";
import { ToolFunction } from "@/types/app/tool";

export function getFileTypeFromVersionId(versionId: string): FileType {
  if (versionId.startsWith(TOOL_VERSION_ID_PREFIX)) {
    return "tool";
  }
  if (versionId.startsWith(PROMPT_VERSION_ID_PREFIX) || versionId.startsWith(CONFIG_ID_PREFIX)) {
    return "prompt";
  }
  if (versionId.startsWith(AGENT_VERSION_ID_PREFIX)) {
    return "agent";
  }
  if (versionId.startsWith(DATASET_VERSION_ID_PREFIX)) {
    return "dataset";
  }
  if (versionId.startsWith(EVALUATOR_VERSION_ID_PREFIX)) {
    return "evaluator";
  }
  if (versionId.startsWith(FLOW_VERSION_ID_PREFIX)) {
    return "flow";
  }
  throw new Error(`Unknown version type for ID: ${versionId}`);
}

export const getFileTypeFromId = (id: string): FileType => {
  if (id.startsWith(PROMPT_ID_PREFIX)) {
    return "prompt";
  } else if (id.startsWith(AGENT_ID_PREFIX)) {
    return "agent";
  } else if (id.startsWith(TOOL_ID_PREFIX)) {
    return "tool";
  } else if (id.startsWith(DATASET_ID_PREFIX) || id.startsWith(DATASET_LEGACY_ID_PREFIX)) {
    return "dataset";
  } else if (id.startsWith(EVALUATOR_ID_PREFIX) || id.startsWith(EVALUATOR_LEGACY_ID_PREFIX)) {
    return "evaluator";
  } else if (id.startsWith(FLOW_ID_PREFIX)) {
    return "flow";
  } else {
    throw new Error(`Unknown file type for id: ${id}`);
  }
};

/**
 * Generates a suggested version name based on an existing version name
 * If the current version name follows the pattern "Name X", increments X
 * Returns an empty string if no current version name is provided
 *
 * @example
 * getNextVersionName("Claude") // "Claude 1"
 * getNextVersionName("Version 1") // "Version 2"
 * getNextVersionName("Version-1") // "Version-2"
 * getNextVersionName("v1") // "v2"
 * getNextVersionName("v1.0") // "v1.1"
 */
export const getNextVersionName = (currentVersionName?: string | null): string => {
  if (!currentVersionName) return "";

  // Check if the string ends with a number and preserve any separators
  const versionRegex = /^(.*?)(\d+)$/;
  const match = currentVersionName.match(versionRegex);

  if (match) {
    const [_, baseName, versionNumber] = match;
    const nextVersionNumber = parseInt(versionNumber, 10) + 1;
    return `${baseName}${nextVersionNumber}`;
  }

  // No version number found, append " 1"
  return `${currentVersionName} 1`;
};

/**
 * Normalizes version metadata fields with different strategies for different field types
 */
export const normalizeVersionFields = {
  /**
   * Normalizes version names: collapses whitespace and converts empty to null
   * @returns null for empty/whitespace-only input, or a normalized string
   */
  name(value?: string | null): string | null {
    if (value === undefined || value === null) return null;
    const trimmed = value.replace(/\s+/g, " ").trim();
    return trimmed === "" ? null : trimmed;
  },

  /**
   * Normalizes version descriptions: only trims edges, preserves internal whitespace
   * @returns null for empty/whitespace-only input, or a trimmed string
   */
  description(value?: string | null): string | null {
    if (value === undefined || value === null) return null;
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
  },
};
